import { isNil } from 'lodash';

const FILTER_CHIP_SELECTOR = '.filter-chip-field';
const MIN_CHIP_WIDTH_FALLBACK_REM = 12;
const MAX_CHIP_WIDTH_FALLBACK_REM = 28;
const ASYNC_RESYNC_DELAYS_MS = [0, 50, 150];
/** Default `--crm-daterangepicker-input-width` from daterangepicker / _filters-ui-v2. */
const DATERANGE_INPUT_WIDTH_DEFAULT_PX = 156;
/**
 * Допуск при сравнении суммы чипов с available.
 * Иначе из‑за safety/+2 в measure поле ровно «под контент», клип срабатывает ложно
 * и text-overflow: ellipsis съедает хвост («…ООО»), хотя справа ещё пусто.
 */
const CHIP_FIT_TOLERANCE_PX = 12;

export interface FilterChipAutosizeOptions {
  /** Полный текст для измерения ширины (например «Выбрано: 2»), когда DOM уже обрезан. */
  resolveMeasureText?: () => string | null;
}

/**
 * Расширяет chip-контейнер (`.filter-chip-field` или хост селекта), когда текст не помещается в базовую ширину.
 * Также поддерживает `crm-daterangepicker` (растёт `--crm-daterangepicker-input-width` + minWidth chip).
 */
export class FilterChipAutosizeHelper {
  private chipField: HTMLElement | null = null;
  private floatLabel: HTMLElement | null = null;
  private control: HTMLElement | null = null;
  private lastAppliedMinWidth: number | null = null;
  private lastAppliedDaterangeInputWidth: number | null = null;
  private isDaterangeMode = false;
  private windowResizeHandler: (() => void) | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private tokenMutationObserver: MutationObserver | null = null;
  private rafId: number | null = null;
  private clipRafId: number | null = null;
  private asyncResyncTimeouts: ReturnType<typeof setTimeout>[] = [];
  /** Пока применяем minWidth — игнор RO, чтобы не запускать второй animated-шаг. */
  private isSyncing = false;
  private isClippingTokens = false;
  private syncingUnlockRafId: number | null = null;

  public constructor(
    private readonly host: HTMLElement,
    private readonly options: FilterChipAutosizeOptions = {}
  ) {}

  public connect(): void {
    this.disconnect();

    this.chipField = this.host.closest(FILTER_CHIP_SELECTOR) ?? this.host;
    this.isDaterangeMode = false;

    const daterangeGroup = this.host.querySelector('.crm-daterangepicker-inputgroup') as HTMLElement | null;
    if (daterangeGroup && this.chipField) {
      this.isDaterangeMode = true;
      this.control = daterangeGroup;
      this.floatLabel = this.host.querySelector('.p-float-label');
      this.observeDaterangeInputResize();
      this.scheduleSync();
      this.scheduleAsyncResyncs();
      this.windowResizeHandler = () => this.scheduleSync();
      window.addEventListener('resize', this.windowResizeHandler, { passive: true });
      return;
    }

    this.floatLabel = this.host.querySelector('.p-float-label');
    this.control = this.resolveControlElement();

    const label = this.floatLabel?.querySelector('label');
    if (!this.chipField || !label || !this.control) {
      return;
    }

    this.observeLabelResize();
    this.observeChipTokenDomChanges();
    this.scheduleSync();
    this.scheduleAsyncResyncs();

    this.windowResizeHandler = () => this.scheduleSync();
    window.addEventListener('resize', this.windowResizeHandler, { passive: true });
  }

  public scheduleSync(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.sync();
    });
  }

  public disconnect(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.clipRafId !== null) {
      cancelAnimationFrame(this.clipRafId);
      this.clipRafId = null;
    }

    if (this.syncingUnlockRafId !== null) {
      cancelAnimationFrame(this.syncingUnlockRafId);
      this.syncingUnlockRafId = null;
    }
    this.isSyncing = false;
    this.isClippingTokens = false;

    this.clearAsyncResyncs();

    if (this.windowResizeHandler) {
      window.removeEventListener('resize', this.windowResizeHandler);
      this.windowResizeHandler = null;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.tokenMutationObserver?.disconnect();
    this.tokenMutationObserver = null;

    if (this.control) {
      const controlInner = this.getControlInnerRoot(this.control);
      const valueLabelEl = controlInner.querySelector(
        '.p-dropdown-label, .p-multiselect-label, .p-treeselect-label'
      ) as HTMLElement | null;
      this.clearChipTokenClipStyles(valueLabelEl);
    }

    this.clearChipMinWidth();
    this.clearDaterangeInputWidth();

    this.chipField = null;
    this.floatLabel = null;
    this.control = null;
    this.lastAppliedMinWidth = null;
    this.isDaterangeMode = false;
  }

  private scheduleAsyncResyncs(): void {
    for (const delay of ASYNC_RESYNC_DELAYS_MS) {
      const timeoutId = setTimeout(() => this.scheduleSync(), delay);
      this.asyncResyncTimeouts.push(timeoutId);
    }
  }

  private clearAsyncResyncs(): void {
    for (const timeoutId of this.asyncResyncTimeouts) {
      clearTimeout(timeoutId);
    }
    this.asyncResyncTimeouts = [];
  }

  private observeLabelResize(): void {
    if (typeof ResizeObserver === 'undefined' || !this.control) {
      return;
    }

    const controlInner = this.getControlInnerRoot(this.control);
    const labelEl = controlInner.querySelector(
      '.p-dropdown-label, .p-multiselect-label, .p-treeselect-label'
    ) as HTMLElement | null;

    if (!labelEl) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (this.isSyncing || this.isClippingTokens) {
        return;
      }
      this.scheduleSync();
    });
    this.resizeObserver.observe(labelEl);
  }

  /** TreeSelect value-template пересоздаёт токены при CD — клип нужно накатывать снова. */
  private observeChipTokenDomChanges(): void {
    if (typeof MutationObserver === 'undefined' || !this.control) {
      return;
    }

    const controlInner = this.getControlInnerRoot(this.control);
    const valueLabelEl = controlInner.querySelector('.p-multiselect-label, .p-treeselect-label') as HTMLElement | null;

    if (!valueLabelEl) {
      return;
    }

    this.tokenMutationObserver?.disconnect();
    this.tokenMutationObserver = new MutationObserver(() => {
      if (this.isSyncing || this.isClippingTokens || !this.control) {
        return;
      }
      const inner = this.getControlInnerRoot(this.control);
      const labelEl = inner.querySelector(
        '.p-multiselect-label, .p-treeselect-label'
      ) as HTMLElement | null;
      this.scheduleClipOverflowingChipTokens(inner, labelEl);
    });
    this.tokenMutationObserver.observe(valueLabelEl, { childList: true, subtree: true });
  }

  private observeDaterangeInputResize(): void {
    if (typeof ResizeObserver === 'undefined' || !this.control) {
      return;
    }

    const input = this.control.querySelector('input.p-inputtext, .p-inputtext') as HTMLElement | null;
    if (!input) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (this.isSyncing) {
        return;
      }
      this.scheduleSync();
    });
    this.resizeObserver.observe(input);
  }

  private syncDaterange(): void {
    const chip = this.chipField;
    const group = this.control;
    if (!chip || !group) {
      return;
    }

    const input = group.querySelector('input.p-inputtext, input.p-component, .p-inputtext') as
      | HTMLInputElement
      | null;
    if (!input) {
      return;
    }

    const text = (this.options.resolveMeasureText?.() ?? input.value ?? '').trim();
    if (!text || text === '\u00A0') {
      this.applyDaterangeWidths(null, null);
      return;
    }

    const stylesSource = this.host.classList.contains('frm-filters-ui-v2') ? this.host : chip;
    const styles = getComputedStyle(stylesSource);
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const maxChipPx = this.getChipMaxWidthPx(chip, styles, rootFontSize);
    const chipMinPx = Math.min(
      this.parseCssLength(getComputedStyle(chip).minWidth, rootFontSize, 10 * rootFontSize, chip.parentElement) ||
        10 * rootFontSize,
      maxChipPx
    );

    const addon = group.querySelector('.p-inputgroup-addon') as HTMLElement | null;
    const addonW = addon?.getBoundingClientRect().width ?? 0;
    const inputStyle = getComputedStyle(input);
    const padX = (parseFloat(inputStyle.paddingLeft) || 0) + (parseFloat(inputStyle.paddingRight) || 0);
    const textW = this.measureTextWidth(input, text);
    const neededInputPx = Math.ceil(textW + padX + 2);
    const maxInputPx = Math.max(DATERANGE_INPUT_WIDTH_DEFAULT_PX, maxChipPx - addonW);
    const targetInputPx = Math.min(Math.max(DATERANGE_INPUT_WIDTH_DEFAULT_PX, neededInputPx), maxInputPx);
    const targetChipPx = Math.min(Math.max(chipMinPx, addonW + targetInputPx), maxChipPx);

    if (
      this.lastAppliedDaterangeInputWidth === targetInputPx &&
      this.lastAppliedMinWidth === targetChipPx
    ) {
      return;
    }

    this.applyDaterangeWidths(targetInputPx, targetChipPx);
  }

  private applyDaterangeWidths(inputWidthPx: number | null, chipMinPx: number | null): void {
    this.beginSyncing();

    if (isNil(inputWidthPx) || isNil(chipMinPx)) {
      this.clearDaterangeInputWidth();
      this.clearChipMinWidth();
      return;
    }

    this.host.style.setProperty('--crm-daterangepicker-input-width', `${inputWidthPx}px`);
    this.lastAppliedDaterangeInputWidth = inputWidthPx;
    this.chipField!.style.minWidth = `${chipMinPx}px`;
    this.lastAppliedMinWidth = chipMinPx;
  }

  private clearDaterangeInputWidth(): void {
    this.host.style.removeProperty('--crm-daterangepicker-input-width');
    this.lastAppliedDaterangeInputWidth = null;
  }

  private sync(): void {
    if (this.isDaterangeMode) {
      this.syncDaterange();
      return;
    }

    const chip = this.chipField;
    const floatLabelEl = this.floatLabel?.querySelector('label') as HTMLElement | null;
    const control = this.control;

    if (!chip || !floatLabelEl || !control) {
      return;
    }

    const controlInner = this.getControlInnerRoot(control);
    const valueLabelEl = controlInner.querySelector(
      '.p-dropdown-label, .p-multiselect-label, .p-treeselect-label'
    ) as HTMLElement | null;
    const selectedText = this.getSelectedDisplayText(valueLabelEl, controlInner);
    const isChipMode = this.isChipDisplayMode(controlInner);
    const hasResolveMeasureText = typeof this.options.resolveMeasureText === 'function';
    const resolvedText = hasResolveMeasureText ? this.options.resolveMeasureText!() : null;

    const stylesSource = this.host.classList.contains('frm-filters-ui-v2') ? this.host : chip;
    const styles = getComputedStyle(stylesSource);
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const minDefaultPx = this.getChipMinWidthPx(styles, rootFontSize);
    const maxPx = this.getChipMaxWidthPx(chip, styles, rootFontSize);

    const trigger =
      (controlInner.querySelector('.p-dropdown-trigger, .p-multiselect-trigger, .p-treeselect-trigger') as
        | HTMLElement
        | null) ?? null;
    const triggerFallback = this.parseCssLength(styles.getPropertyValue('--frm-filters-trigger-width'), rootFontSize, 36);
    const triggerW = trigger?.offsetWidth || triggerFallback;

    let needed: number;
    let forceApplyMinWidth = false;

    if (selectedText) {
      const tokenCount = isChipMode
        ? controlInner.querySelectorAll('.p-multiselect-token, .p-treeselect-token').length
        : 0;
      // Selection есть, но options/лейбл ещё не готовы — не расширять по обрезанному DOM.
      // Для chip-режима null из resolveMeasureText нормален: ширину меряем по токенам/title.
      if (hasResolveMeasureText && isNil(resolvedText) && tokenCount <= 1 && !isChipMode) {
        return;
      }

      if (isChipMode) {
        // Не сбрасываем clip до пересчёта — иначе вспышка полных чипов справа.
        // measureChipTokensNeededWidth меряет через text probe, не через clipped DOM.
        needed = this.measureChipTokensNeededWidth(
          controlInner,
          valueLabelEl,
          triggerW,
          resolvedText
        );
        if (needed > minDefaultPx) {
          forceApplyMinWidth = true;
        }
      } else {
        const measureFrom = this.getMeasureElement(valueLabelEl) ?? valueLabelEl ?? floatLabelEl;
        const fullText = resolvedText ?? this.getFullSelectedText(valueLabelEl, controlInner, selectedText);
        needed = this.measureFilledNeededWidth(valueLabelEl, measureFrom, fullText, triggerW);
        forceApplyMinWidth = !!resolvedText;

        if (!resolvedText && valueLabelEl && valueLabelEl.scrollWidth > valueLabelEl.clientWidth + 1) {
          forceApplyMinWidth = true;
        }
      }
    } else {
      const floatText = floatLabelEl.textContent?.trim() ?? '';
      if (!floatText) {
        this.clearChipTokenClipStyles(valueLabelEl);
        this.applyChipMinWidth('');
        return;
      }

      const pl = this.parseCssLength(styles.getPropertyValue('--frm-filters-label-pl'), rootFontSize, 20);
      const pr = this.parseCssLength(styles.getPropertyValue('--frm-filters-label-pr'), rootFontSize, 12);
      const gap = this.parseCssLength(styles.getPropertyValue('--frm-filters-label-to-trigger-gap'), rootFontSize, 8);
      needed = Math.ceil(this.measureTextWidth(floatLabelEl, floatText) + pl + pr + gap + triggerW);

      if (needed <= minDefaultPx) {
        this.clearChipTokenClipStyles(valueLabelEl);
        this.applyChipMinWidth('');
        return;
      }
    }

    if (needed <= minDefaultPx && !forceApplyMinWidth) {
      this.applyChipMinWidth('');
      if (isChipMode) {
        this.clipOverflowingChipTokensAfterLayout(controlInner, valueLabelEl);
      } else {
        this.clearChipTokenClipStyles(valueLabelEl);
      }
      return;
    }

    const targetMinPx = Math.min(Math.max(minDefaultPx, needed), maxPx);

    if (this.lastAppliedMinWidth !== targetMinPx) {
      this.applyChipMinWidth(`${targetMinPx}px`, targetMinPx);
    }

    if (isChipMode) {
      this.clipOverflowingChipTokensAfterLayout(controlInner, valueLabelEl);
    }
  }

  /**
   * Клип после forced reflow в том же кадре sync — без вспышки полных чипов.
   * Для MutationObserver (пересоздание DOM) — один rAF.
   */
  private scheduleClipOverflowingChipTokens(controlInner: HTMLElement, valueLabelEl: HTMLElement | null): void {
    if (this.clipRafId !== null) {
      cancelAnimationFrame(this.clipRafId);
    }
    this.clipRafId = requestAnimationFrame(() => {
      this.clipRafId = null;
      this.clipOverflowingChipTokensAfterLayout(controlInner, valueLabelEl);
    });
  }

  private clipOverflowingChipTokensAfterLayout(
    controlInner: HTMLElement,
    valueLabelEl: HTMLElement | null
  ): void {
    if (this.clipRafId !== null) {
      cancelAnimationFrame(this.clipRafId);
      this.clipRafId = null;
    }
    if (this.chipField) {
      void this.chipField.offsetWidth;
    }
    if (valueLabelEl) {
      void valueLabelEl.offsetWidth;
    }
    this.clipOverflowingChipTokens(controlInner, valueLabelEl);
  }

  private clearChipTokenClipStyles(valueLabelEl: HTMLElement | null): void {
    if (!valueLabelEl) {
      return;
    }

    const tokens = Array.from(valueLabelEl.querySelectorAll('.p-multiselect-token, .p-treeselect-token')) as HTMLElement[];

    for (const token of tokens) {
      token.style.maxWidth = '';
      token.style.display = '';
      token.style.minWidth = '';
      const tokenLabel = token.querySelector('.p-multiselect-token-label, .p-treeselect-token-label') as HTMLElement | null;
      if (tokenLabel) {
        // Снимаем клип и снова снимаем Prime max-width: 100px
        tokenLabel.style.removeProperty('max-width');
        tokenLabel.style.setProperty('max-width', 'none', 'important');
        tokenLabel.style.minWidth = '';
        tokenLabel.style.flex = '';
        tokenLabel.style.overflow = '';
        tokenLabel.style.textOverflow = '';
        tokenLabel.style.whiteSpace = '';
      }
    }
  }

  private applyTokenEllipsisClip(token: HTMLElement, maxWidthPx: number): void {
    token.style.maxWidth = `${Math.floor(maxWidthPx)}px`;
    token.style.minWidth = '0';
    token.style.display = '';
    const tokenLabel = token.querySelector('.p-multiselect-token-label, .p-treeselect-token-label') as HTMLElement | null;
    if (tokenLabel) {
      tokenLabel.style.minWidth = '0';
      tokenLabel.style.flex = '1 1 0';
      tokenLabel.style.setProperty('max-width', '100%', 'important');
      tokenLabel.style.overflow = 'hidden';
      tokenLabel.style.textOverflow = 'ellipsis';
      tokenLabel.style.whiteSpace = 'nowrap';
    }
  }

  /**
   * Правая граница области чипов (client area лейбла без padding-right под ✕).
   */
  private getChipTokensContentRight(valueLabelEl: HTMLElement): number {
    const labelRect = valueLabelEl.getBoundingClientRect();
    const padRight = parseFloat(getComputedStyle(valueLabelEl).paddingRight) || 0;

    return labelRect.right - padRight;
  }

  private clipOverflowingChipTokens(controlInner: HTMLElement, valueLabelEl: HTMLElement | null): void {
    if (!valueLabelEl || !this.isChipDisplayMode(controlInner)) {
      return;
    }

    const tokens = Array.from(valueLabelEl.querySelectorAll('.p-multiselect-token, .p-treeselect-token')) as HTMLElement[];

    this.isClippingTokens = true;
    this.clearChipTokenClipStyles(valueLabelEl);

    if (!tokens.length) {
      this.isClippingTokens = false;
      return;
    }

    // Forced reflow: меряем layout после applyChipMinWidth (не lastApplied − trigger).
    void valueLabelEl.offsetWidth;
    const contentRight = this.getChipTokensContentRight(valueLabelEl);
    if (contentRight <= 0) {
      this.isClippingTokens = false;
      return;
    }

    /** Минимум, чтобы на лейбле гарантированно отрисовался text-overflow: ellipsis. */
    const minEllipsisTokenPx = 40;

    for (let i = 0; i < tokens.length; i += 1) {
      const token = tokens[i];
      const tokenRect = token.getBoundingClientRect();
      const marginRight = parseFloat(getComputedStyle(token).marginRight) || 0;
      const tokenRight = tokenRect.right + marginRight;

      if (tokenRight <= contentRight + CHIP_FIT_TOLERANCE_PX) {
        continue;
      }

      // Чип вылез за правый край контента — обрезаем его до границы, остальные скрываем.
      const maxBorderW = contentRight - tokenRect.left - marginRight;
      this.beginSyncing();

      if (maxBorderW >= minEllipsisTokenPx) {
        this.applyTokenEllipsisClip(token, maxBorderW);
        for (let j = i + 1; j < tokens.length; j += 1) {
          tokens[j].style.display = 'none';
        }
      } else if (i > 0) {
        // Остатка < 40px: ужимаем предыдущий чип, чтобы освободить место под stub
        // переполняющего с ellipsis (как у длинных чипов multiselect).
        const prev = tokens[i - 1];
        const prevRect = prev.getBoundingClientRect();
        const prevNaturalW = prevRect.width;
        const needed = minEllipsisTokenPx - maxBorderW;
        const shrunkPrevW = Math.max(minEllipsisTokenPx, Math.floor(prevNaturalW - needed));

        if (shrunkPrevW < prevNaturalW) {
          this.applyTokenEllipsisClip(prev, shrunkPrevW);
          void prev.offsetWidth;
          this.applyTokenEllipsisClip(token, minEllipsisTokenPx);
          for (let j = i + 1; j < tokens.length; j += 1) {
            tokens[j].style.display = 'none';
          }
        } else {
          // Предыдущий нельзя ужать — скрываем переполняющий, ellipsis на prev до края.
          const prevMargin = parseFloat(getComputedStyle(prev).marginRight) || 0;
          const prevMax = contentRight - prevRect.left - prevMargin;
          this.applyTokenEllipsisClip(prev, Math.max(minEllipsisTokenPx, Math.floor(prevMax)));
          for (let j = i; j < tokens.length; j += 1) {
            tokens[j].style.display = 'none';
          }
        }
      } else {
        this.applyTokenEllipsisClip(token, Math.max(minEllipsisTokenPx, Math.floor(maxBorderW)));
      }

      this.isClippingTokens = false;
      return;
    }

    this.isClippingTokens = false;
  }

  private applyChipMinWidth(minWidth: string, appliedPx: number | null = null): void {
    if (!this.chipField) {
      return;
    }

    if (minWidth === '' && this.lastAppliedMinWidth === null && !this.chipField.style.minWidth) {
      return;
    }

    this.beginSyncing();
    this.chipField.style.minWidth = minWidth;
    this.lastAppliedMinWidth = appliedPx;
  }

  private beginSyncing(): void {
    this.isSyncing = true;
    if (this.syncingUnlockRafId !== null) {
      cancelAnimationFrame(this.syncingUnlockRafId);
    }
    // RO обычно срабатывает после layout — снимаем флаг на следующем кадре.
    this.syncingUnlockRafId = requestAnimationFrame(() => {
      this.syncingUnlockRafId = null;
      this.isSyncing = false;
    });
  }

  private clearChipMinWidth(): void {
    if (this.chipField) {
      this.chipField.style.minWidth = '';
    }
    this.lastAppliedMinWidth = null;
  }

  private getSelectedDisplayText(valueLabelEl: HTMLElement | null, controlInner: HTMLElement): string | null {
    if (!valueLabelEl || valueLabelEl.classList.contains('p-placeholder')) {
      return null;
    }

    if (this.isChipDisplayMode(controlInner)) {
      const tokens = controlInner.querySelectorAll('.p-multiselect-token, .p-treeselect-token');
      if (tokens.length > 0) {
        return Array.from(tokens)
          .map((token) => token.textContent?.trim() ?? '')
          .filter(Boolean)
          .join(' ');
      }
    }

    const text = valueLabelEl.textContent?.trim();
    return text || null;
  }

  /**
   * textContent label может совпадать с визуально обрезанным текстом; title часто содержит полное значение.
   */
  private getFullSelectedText(
    valueLabelEl: HTMLElement | null,
    controlInner: HTMLElement,
    fallback: string
  ): string {
    const title =
      valueLabelEl?.getAttribute('title')?.trim() ||
      valueLabelEl
        ?.querySelector('.color-black span, .p-multiselect-token-label, .p-treeselect-token-label')
        ?.getAttribute('title')
        ?.trim();

    if (title) {
      return title;
    }

    if (this.isChipDisplayMode(controlInner)) {
      return fallback;
    }

    const measureEl = this.getMeasureElement(valueLabelEl);
    if (measureEl && measureEl.scrollWidth > measureEl.clientWidth + 1) {
      return measureEl.textContent?.trim() || fallback;
    }

    return fallback;
  }

  private isChipDisplayMode(controlInner: HTMLElement): boolean {
    return (
      controlInner.classList.contains('p-multiselect-chip') ||
      controlInner.classList.contains('p-treeselect-chip') ||
      !!controlInner.querySelector('.p-multiselect-token, .p-treeselect-token')
    );
  }

  private resolveControlElement(): HTMLElement | null {
    return (
      (this.host.querySelector('p-dropdown, p-multiselect, p-treeselect') as HTMLElement | null) ??
      (this.host.querySelector('.p-dropdown, .p-multiselect, .p-treeselect') as HTMLElement | null)
    );
  }

  private getControlInnerRoot(control: HTMLElement): HTMLElement {
    return (
      (control.querySelector('.p-dropdown, .p-multiselect, .p-treeselect') as HTMLElement | null) ?? control
    );
  }

  private getMeasureElement(valueLabelEl: HTMLElement | null): HTMLElement | null {
    if (!valueLabelEl) {
      return null;
    }

    return (
      (valueLabelEl.querySelector(
        '.p-multiselect-token-label, .p-treeselect-token-label, .color-black span, span'
      ) as HTMLElement | null) ?? valueLabelEl
    );
  }

  private measureFilledNeededWidth(
    valueLabelEl: HTMLElement | null,
    measureFrom: HTMLElement,
    text: string,
    triggerW: number
  ): number {
    if (!valueLabelEl) {
      return Math.ceil(this.measureTextWidth(measureFrom, text) + triggerW);
    }

    const labelStyle = getComputedStyle(valueLabelEl);
    const labelPadX = parseFloat(labelStyle.paddingLeft) + parseFloat(labelStyle.paddingRight);

    return Math.ceil(labelPadX + this.measureTextWidth(measureFrom, text) + triggerW) + 2;
  }

  private measureChipTokensNeededWidth(
    controlInner: HTMLElement,
    valueLabelEl: HTMLElement | null,
    triggerW: number,
    resolvedText: string | null = null
  ): number {
    const tokens = Array.from(
      controlInner.querySelectorAll('.p-multiselect-token, .p-treeselect-token')
    ) as HTMLElement[];

    let tokensW = 0;

    for (let i = 0; i < tokens.length; i += 1) {
      const token = tokens[i];
      const tokenLabel = token.querySelector(
        '.p-multiselect-token-label, .p-treeselect-token-label'
      ) as HTMLElement | null;
      const measureFrom = tokenLabel ?? token;
      const text =
        (tokens.length === 1 && resolvedText) ||
        measureFrom.getAttribute('title')?.trim() ||
        token.getAttribute('title')?.trim() ||
        measureFrom.textContent?.trim() ||
        '';
      const tokenStyle = getComputedStyle(token);
      const tokenPadX = parseFloat(tokenStyle.paddingLeft) + parseFloat(tokenStyle.paddingRight);
      const tokenMarginRight = parseFloat(tokenStyle.marginRight) || 0;
      const tokenIcon = token.querySelector(
        '.p-multiselect-token-icon, .p-treeselect-token-icon'
      ) as HTMLElement | null;
      const iconW = tokenIcon?.offsetWidth ?? 0;
      const iconMargin = tokenIcon ? parseFloat(getComputedStyle(tokenIcon).marginLeft) || 0 : 0;

      tokensW +=
        this.measureTextWidth(measureFrom, text) + tokenPadX + iconW + iconMargin + tokenMarginRight;
    }

    const labelStyle = valueLabelEl ? getComputedStyle(valueLabelEl) : null;
    const labelPadX = labelStyle
      ? parseFloat(labelStyle.paddingLeft) + parseFloat(labelStyle.paddingRight)
      : 0;

    // labelPadX уже включает резерв под ✕ (padding-right clearable) — clearW не дублируем.
    // +CHIP_FIT_TOLERANCE_PX: поле чуть шире контента, чтобы клип не срабатывал ложно.
    return Math.ceil(labelPadX + tokensW + triggerW) + 2 + CHIP_FIT_TOLERANCE_PX;
  }

  private measureTextWidth(source: HTMLElement, text: string): number {
    const probe = document.createElement('span');
    probe.textContent = text;
    probe.style.cssText =
      'position:absolute;left:-9999px;top:-9999px;visibility:hidden;white-space:nowrap;pointer-events:none;';
    const style = getComputedStyle(source);
    probe.style.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    probe.style.letterSpacing = style.letterSpacing;

    document.body.appendChild(probe);
    const width = probe.getBoundingClientRect().width;
    document.body.removeChild(probe);

    return width;
  }

  private getChipMinWidthPx(styles: CSSStyleDeclaration, rootFontSize: number): number {
    const fromToken = this.parseCssLength(
      styles.getPropertyValue('--frm-filters-chip-min-width'),
      rootFontSize,
      0
    );
    if (fromToken > 0 && Number.isFinite(fromToken)) {
      return fromToken;
    }

    return MIN_CHIP_WIDTH_FALLBACK_REM * rootFontSize;
  }

  private getChipMaxWidthPx(
    chip: HTMLElement,
    styles: CSSStyleDeclaration,
    rootFontSize: number
  ): number {
    const chipMaxWidth = getComputedStyle(chip).maxWidth;
    const parsedChip = this.parseCssLength(chipMaxWidth, rootFontSize, 0, chip.parentElement);

    if (parsedChip > 0 && Number.isFinite(parsedChip)) {
      return parsedChip;
    }

    const tokenMax = this.parseCssLength(
      styles.getPropertyValue('--frm-filters-chip-max-width'),
      rootFontSize,
      0
    );

    if (tokenMax > 0 && Number.isFinite(tokenMax)) {
      return tokenMax;
    }

    return MAX_CHIP_WIDTH_FALLBACK_REM * rootFontSize;
  }

  private parseCssLength(
    raw: string,
    rootFontSize: number,
    fallbackPx: number,
    percentRef?: HTMLElement | null
  ): number {
    const value = raw?.trim();
    if (!value || value === 'none') {
      return fallbackPx;
    }
    if (value.endsWith('rem')) {
      return parseFloat(value) * rootFontSize;
    }
    if (value.endsWith('px')) {
      return parseFloat(value);
    }
    if (value.endsWith('%')) {
      const pct = parseFloat(value);
      const ref = percentRef ?? this.chipField?.parentElement;
      if (ref && Number.isFinite(pct)) {
        const refWidth = ref.getBoundingClientRect().width;
        if (refWidth > 0) {
          return (pct / 100) * refWidth;
        }
      }
      return fallbackPx;
    }
    const numeric = parseFloat(value);
    return Number.isFinite(numeric) ? numeric : fallbackPx;
  }
}
